import { getProductViewsMetrics } from "@/infrastructure/services/metricsService";
import facebookLogo from "@/assets/logos/facebook.svg";
import instagramLogo from "@/assets/logos/instagram.svg";
import tiktokLogo from "@/assets/logos/tiktok.svg";
import whatsappLogo from "@/assets/logos/whatsapp.svg";
import type {
  ProductVisitOrigin,
  ProductViewsMetrics,
} from "@/types/profileMetrics";
import {
  Dialog,
  Icon,
  Loading,
  Tooltip,
  type IconName,
} from "@uigovpe/components";
import { useEffect, useState } from "react";
import PerformanceHighlight from "./PerformanceHighlight";

type Props = {
  visible: boolean;
  onHide: () => void;
};

const originIcons: Record<string, IconName> = {
  link: "share",
  compartilhado: "share",
  busca: "search",
  plataforma: "search",
  instagram: "photo_camera",
  tiktok: "music_note",
  facebook: "public",
  whatsapp: "call",
};

const socialLogos: Record<string, string> = {
  facebook: facebookLogo,
  instagram: instagramLogo,
  tiktok: tiktokLogo,
  whatsapp: whatsappLogo,
};

function iconForOrigin(origin: string): IconName {
  const normalized = origin.toLocaleLowerCase("pt-BR");
  const match = Object.entries(originIcons).find(([name]) =>
    normalized.includes(name),
  );
  return match?.[1] ?? "visibility";
}

function logoForOrigin(origin: string) {
  const normalized = origin.toLocaleLowerCase("pt-BR");
  return Object.entries(socialLogos).find(([name]) =>
    normalized.includes(name),
  )?.[1];
}

function formatPercentage(value: number) {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

function formatVariation(value: number | null) {
  return value === null ? "Sem comparação" : `${formatPercentage(value)}%`;
}

function originTooltip(origin: ProductVisitOrigin) {
  const participation = `${formatPercentage(origin.percentual)}% das visitas do período vieram de ${origin.label}.`;

  if (origin.variacaoPercentual === null)
    return `${participation} Não há dados do período anterior para comparação.`;
  if (origin.variacaoPercentual === 0)
    return `${participation} Não houve variação em relação ao período anterior.`;

  const direction = origin.variacaoPercentual > 0 ? "aumento" : "redução";
  return `${participation} Houve ${direction} de ${formatPercentage(Math.abs(origin.variacaoPercentual))}% em relação ao período anterior.`;
}

export default function ProductViewsDialog({ visible, onHide }: Props) {
  const [origins, setOrigins] = useState<ProductVisitOrigin[]>([]);
  const [bestPerformance, setBestPerformance] =
    useState<ProductViewsMetrics["melhorDesempenho"]>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasManyOrigins = origins.length > 4;

  useEffect(() => {
    if (!visible) return;
    let active = true;
    setIsLoading(true);
    setError(null);
    getProductViewsMetrics("7d")
      .then((data) => {
        if (!active) return;
        setOrigins(data.origensVisitas ?? []);
        setBestPerformance(data.melhorDesempenho ?? null);
      })
      .catch(() => {
        if (active)
          setError("Não foi possível carregar as visualizações dos produtos.");
      })
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, [visible]);

  return (
    <Dialog
      visible={visible}
      header={
        <span className="pt-1! text-[20px]! leading-7! font-bold! text-[#494c57]!">
          De onde vêm as visitas
        </span>
      }
      onHide={onHide}
      draggable={false}
      closable={false}
      className={`profile-views-dialog bg-white! ${
        hasManyOrigins
          ? "w-[min(94vw,960px)]! max-w-[min(94vw,960px)]!"
          : "w-[min(94vw,560px)]! max-w-[min(94vw,560px)]!"
      }`}
    >
      <section className="rounded-2xl! px-4! pb-0! sm:px-6! sm:pb-6!">
        {isLoading ? (
          <div
            className="flex! h-64! items-center! justify-center!"
            role="status"
          >
            <Loading className="text-[#4e3873]!" />
            <span className="sr-only">
              Carregando visualizações dos produtos...
            </span>
          </div>
        ) : error ? (
          <div
            className="flex! h-64! items-center! justify-center! text-sm! text-red-700!"
            role="alert"
          >
            {error}
          </div>
        ) : (
          <>
            {origins.length > 0 && (
              <div
                className={`grid! grid-cols-1! gap-3! ${
                  hasManyOrigins ? "min-[900px]:grid-cols-2!" : ""
                }`}
              >
                {origins.map((origin, index) => {
                  const socialLogo = logoForOrigin(origin.origem);
                  const tooltipId = `product-origin-${index}`;
                  return (
                    <div key={origin.origem}>
                      <div
                        id={tooltipId}
                        tabIndex={0}
                        aria-label={originTooltip(origin)}
                        className="grid! min-h-18! grid-cols-[2.5rem_minmax(0,1fr)_3rem]! items-center! gap-2! rounded-xl! bg-white! px-3! py-3! shadow-md! focus-visible:outline-2! focus-visible:outline-offset-2! focus-visible:outline-[#4e3873]! min-[480px]:grid-cols-[2.5rem_minmax(8rem,1fr)_minmax(5rem,7rem)_3rem]! min-[480px]:gap-3! min-[480px]:px-4!"
                      >
                        <span className="row-span-2! flex! h-10! w-10! items-center! justify-center! rounded-full! bg-[#f6f2fc]! min-[480px]:row-auto!">
                          {socialLogo ? (
                            <img
                              src={socialLogo}
                              alt=""
                              aria-hidden="true"
                              className="h-6! w-6! object-contain!"
                            />
                          ) : (
                            <Icon
                              icon={iconForOrigin(origin.origem)}
                              outline
                              className="text-[#4e3873]!"
                            />
                          )}
                        </span>
                        <span className="min-w-0! text-xs! leading-tight! font-semibold! text-[#494c57]!">
                          {origin.label}
                          <small className="block! font-normal! text-[#9ca3af]!">
                            {formatVariation(origin.variacaoPercentual)}
                          </small>
                        </span>
                        <span className="col-span-2! col-start-2! row-start-2! h-1! overflow-hidden! rounded-full! bg-[#e5e7eb]! min-[480px]:col-auto! min-[480px]:row-auto!">
                          <progress
                            className="block! h-full! w-full! appearance-none! overflow-hidden! rounded-full! [&::-moz-progress-bar]:bg-[#4e3873]! [&::-webkit-progress-bar]:bg-transparent! [&::-webkit-progress-value]:bg-[#4e3873]!"
                            value={Math.min(100, Math.max(0, origin.percentual))}
                            max="100"
                            aria-label={`${formatPercentage(origin.percentual)}% das visitas`}
                          />
                        </span>
                        <strong className="whitespace-nowrap! text-right! text-[10px]! text-[#64748b]!">
                          {formatPercentage(origin.percentual)}%
                        </strong>
                      </div>
                      <Tooltip
                        target={`#${tooltipId}`}
                        theme="light"
                        position="mouse"
                        mouseTrack
                        mouseTrackTop={12}
                        mouseTrackLeft={12}
                        content={
                          <span className="block! w-60! whitespace-normal">
                            {originTooltip(origin)}
                          </span>
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {origins.length === 0 && (
              <div className="flex! h-40! items-center! justify-center! text-sm! text-[#6b7280]!">
                Nenhuma origem de visita nos últimos 7 dias.
              </div>
            )}

            {bestPerformance && (
              <PerformanceHighlight>
                {bestPerformance.variacaoPercentual !== null &&
                bestPerformance.variacaoPercentual > 0 ? (
                  <>
                    Seus produtos tiveram{" "}
                    <strong>
                      {formatPercentage(bestPerformance.variacaoPercentual)}%
                      mais visitas por {bestPerformance.label}
                    </strong>{" "}
                    esta semana comparando à anterior.
                  </>
                ) : (
                  <>
                    <strong>{bestPerformance.label}</strong> foi a origem com
                    melhor desempenho nos últimos 7 dias, com{" "}
                    <strong>
                      {bestPerformance.total.toLocaleString("pt-BR")} visitas
                    </strong>
                    .
                  </>
                )}
              </PerformanceHighlight>
            )}
          </>
        )}
      </section>
    </Dialog>
  );
}
